/**
 * MapWidget - Carte géographique interactive
 * 
 * Affiche des données sur une carte du monde avec react-simple-maps.
 * Supporte le mode day/night avec des couleurs dynamiques.
 */

import React, { useState, useMemo, useEffect } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup,
} from "react-simple-maps";
import { Typography } from "@/components/ui";
import { FaMapMarkedAlt } from "react-icons/fa";
import { Z_INDEX, zIndexClass } from "@/utils/z-index";
import type { DocumentsData, MapVisualConfig } from "@/models/widget-v2";
import { chartColors, vibrantColors, daySurfaces, nightSurfaces, greyScale } from "@/styles/colors";

// ============================================================================
// Theme-aware Map Colors Hook
// ============================================================================

/**
 * Hook to get theme-aware colors for the map
 * Reads CSS variables to support day/night mode
 */
function useMapThemeColors() {
  const [colors, setColors] = useState({
    geoFill: daySurfaces.border,
    geoStroke: greyScale[400],
    geoHoverFill: daySurfaces.borderStrong,
    geoHoverStroke: greyScale[600],
    geoPressedFill: greyScale[400],
    geoPressedStroke: greyScale[700],
    textFill: greyScale[800],
    markerStroke: daySurfaces.surfaceElevated,
  });

  useEffect(() => {
    const updateColors = () => {
      const root = document.documentElement;
      const computedStyle = getComputedStyle(root);
      
      // Check if we're in night mode
      const isNightMode = root.classList.contains("theme-night");
      
      if (isNightMode) {
        // Night mode colors - darker map with lighter borders
        setColors({
          geoFill: (computedStyle.getPropertyValue("--arc-surface-tertiary").trim() || "#232330") as any,
          geoStroke: (computedStyle.getPropertyValue("--arc-border").trim() || "#4a4a5a") as any,
          geoHoverFill: (computedStyle.getPropertyValue("--arc-surface-secondary").trim() || "#282834") as any,
          geoHoverStroke: (computedStyle.getPropertyValue("--arc-border-strong").trim() || "#5a5a6a") as any,
          geoPressedFill: (computedStyle.getPropertyValue("--arc-surface").trim() || "#2d2d3a") as any,
          geoPressedStroke: (computedStyle.getPropertyValue("--arc-accent").trim() || "#6366F1") as any,
          textFill: (computedStyle.getPropertyValue("--arc-content-primary").trim() || "#f0f0f5") as any,
          markerStroke: (computedStyle.getPropertyValue("--arc-surface-elevated").trim() || "#383848") as any,
        });
      } else {
        // Day mode colors - light map with subtle borders
        setColors({
          geoFill: (computedStyle.getPropertyValue("--arc-surface-tertiary").trim() || "#EBEDF0") as any,
          geoStroke: (computedStyle.getPropertyValue("--arc-border").trim() || "#E5E7EB") as any,
          geoHoverFill: (computedStyle.getPropertyValue("--arc-surface-secondary").trim() || "#F4F5F7") as any,
          geoHoverStroke: (computedStyle.getPropertyValue("--arc-border-strong").trim() || "#D1D5DB") as any,
          geoPressedFill: (computedStyle.getPropertyValue("--arc-surface").trim() || "#FAFBFC") as any,
          geoPressedStroke: (computedStyle.getPropertyValue("--arc-accent").trim() || "#6366F1") as any,
          textFill: (computedStyle.getPropertyValue("--arc-content-primary").trim() || "#1F2937") as any,
          markerStroke: (computedStyle.getPropertyValue("--arc-surface-elevated").trim() || "#FFFFFF") as any,
        });
      }
    };

    // Initial update
    updateColors();

    // Listen for theme changes via class mutations on root
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === "class") {
          updateColors();
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });

    return () => observer.disconnect();
  }, []);

  return colors;
}

// Types pour éviter les erreurs TypeScript
const ComposableMapComponent = ComposableMap as any;
const ZoomableGroupComponent = ZoomableGroup as any;
const GeographiesComponent = Geographies as any;
const GeographyComponent = Geography as any;
const MarkerComponent = Marker as any;

// ============================================================================
// Types
// ============================================================================

interface MapDataPoint {
  country: string;
  latitude: number;
  longitude: number;
  value: number;
  color?: string;
  label?: string;
}

interface MapWidgetProps {
  data: DocumentsData | { type: string; data: MapDataPoint[] };
  visual: MapVisualConfig;
  title?: string;
}

// ============================================================================
// Main Component
// ============================================================================

export const MapWidget: React.FC<MapWidgetProps> = ({
  data,
  visual,
  title,
}) => {
  const [tooltipContent, setTooltipContent] = useState<string>("");
  const mapColors = useMapThemeColors();
  
  // Configuration depuis visual avec valeurs par défaut
  const projection = visual.projection ?? "mercator";
  const defaultZoom = visual.defaultZoom ?? 1;
  const showMarkerLabels = visual.showMarkerLabels ?? true;

  // Extract and validate map data
  const mapData: MapDataPoint[] = React.useMemo(() => {
    if (!data) return [];
    
    // Format: { type: "world", data: [...] }
    if ("data" in data && Array.isArray((data as any).data)) {
      return (data as any).data.filter((item: any): item is MapDataPoint => {
        return item &&
          item.country &&
          typeof item.country === "string" &&
          typeof item.latitude === "number" &&
          !isNaN(item.latitude) &&
          typeof item.longitude === "number" &&
          !isNaN(item.longitude) &&
          typeof item.value === "number" &&
          !isNaN(item.value);
      });
    }
    
    // Format: DocumentsData with documents containing geo data
    if ("documents" in data && Array.isArray(data.documents)) {
      const colors = visual.colors || [chartColors.blue, chartColors.green, vibrantColors[0], chartColors.red, chartColors.violet];
      const result: MapDataPoint[] = [];
      
      data.documents.forEach((doc: any, index: number) => {
        const lat = doc.latitude || doc.lat || doc.normalized?.latitude;
        const lng = doc.longitude || doc.lng || doc.lon || doc.normalized?.longitude;
        const value = doc.value || doc.count || 1;
        const name = doc.country || doc.name || doc.normalized?.title || `Point ${index + 1}`;
        
        if (typeof lat === "number" && typeof lng === "number" && !isNaN(lat) && !isNaN(lng)) {
          result.push({
            country: name,
            latitude: lat,
            longitude: lng,
            value: value,
            color: colors[index % colors.length],
            label: `${name} - ${value}`,
          });
        }
      });
      
      return result;
    }
    
    return [];
  }, [data, visual.colors]);

  if (mapData.length === 0) {
    return (
      <div className="h-full w-full flex flex-col">
        {title && (
          <div className="px-4 pt-4 pb-2">
            <Typography variant="h6" className="text-content-primary">{title}</Typography>
          </div>
        )}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <FaMapMarkedAlt className="text-6xl text-content-tertiary mx-auto mb-4" />
            <Typography variant="h6" className="text-content-secondary">
              Aucune donnée géographique disponible
            </Typography>
            <Typography variant="text-sm" className="text-content-tertiary mt-2">
              Les données nécessitent des coordonnées latitude/longitude
            </Typography>
          </div>
        </div>
      </div>
    );
  }

  const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

  return (
    <div className="h-full w-full flex flex-col overflow-hidden">
      <div className="w-full h-full bg-transparent rounded-lg p-4 flex flex-col overflow-hidden">
        {title && (
          <h3 className="text-lg font-semibold text-info-dark mb-4">{title}</h3>
        )}
        
        <div className="flex-1 relative overflow-hidden rounded-lg">
          <ComposableMapComponent
            projection={`geo${projection.charAt(0).toUpperCase() + projection.slice(1)}`}
            projectionConfig={{ scale: 100 * defaultZoom, center: [0, 20] }}
            style={{ width: "100%", height: "100%", overflow: "hidden" }}
          >
            <ZoomableGroupComponent zoom={defaultZoom}>
              <GeographiesComponent geography={geoUrl}>
                {({ geographies }: any) =>
                  geographies.map((geo: any) => (
                    <GeographyComponent
                      key={geo.rsmKey}
                      geography={geo}
                      fill={mapColors.geoFill}
                      stroke={mapColors.geoStroke}
                      strokeWidth={0.5}
                      style={{
                        default: { fill: mapColors.geoFill, stroke: mapColors.geoStroke, strokeWidth: 0.5, outline: "none" },
                        hover: { fill: mapColors.geoHoverFill, stroke: mapColors.geoHoverStroke, strokeWidth: 1, outline: "none" },
                        pressed: { fill: mapColors.geoPressedFill, stroke: mapColors.geoPressedStroke, strokeWidth: 1, outline: "none" },
                      }}
                    />
                  ))
                }
              </GeographiesComponent>
              
              {mapData.map(({ country, latitude, longitude, value, color, label }) => (
                <MarkerComponent
                  key={country}
                  coordinates={[longitude, latitude]}
                  onMouseEnter={() => setTooltipContent(label || `${country}: ${value}`)}
                  onMouseLeave={() => setTooltipContent("")}
                >
                  <circle
                    r={Math.max(4, Math.min(20, value / 50))}
                    fill={color || chartColors.blue}
                    stroke={mapColors.markerStroke}
                    strokeWidth={2}
                    style={{ cursor: "pointer" }}
                  />
                  {showMarkerLabels && (
                    <text
                      textAnchor="middle"
                      y={-Math.max(4, Math.min(20, value / 50)) - 5}
                      style={{
                        fontFamily: "system-ui",
                        fill: mapColors.textFill,
                        fontSize: "10px",
                        fontWeight: "bold",
                      }}
                    >
                      {value}
                    </text>
                  )}
                </MarkerComponent>
              ))}
            </ZoomableGroupComponent>
          </ComposableMapComponent>

          {/* Tooltip */}
          {tooltipContent && (
            <div className={`absolute top-2 left-2 bg-surface-elevated border border-arc-border rounded shadow-arc-lg p-2 text-sm text-content-primary ${zIndexClass(Z_INDEX.TOOLTIP)} max-w-xs`}>
              {tooltipContent}
            </div>
          )}

          {/* Legend */}
          {(visual.showLegend ?? true) && (
            <div className="absolute bottom-2 right-2 bg-surface-elevated/90 backdrop-blur-sm rounded-lg p-2 text-xs border border-arc-border">
              <div className="font-semibold text-content-primary mb-1">Légende</div>
              <div className="space-y-1">
                {mapData.slice(0, 10).map(({ country, color, value }) => (
                  <div key={country} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color || chartColors.blue }} />
                    <span className="text-content-secondary">{country}: {value}</span>
                  </div>
                ))}
                {mapData.length > 10 && (
                  <div className="text-content-tertiary italic">+{mapData.length - 10} autres...</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MapWidget;
